import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Pencil, Zap } from 'lucide-react';
import { isAxiosError } from 'axios';
import { apiClient } from '@/lib/apiClient';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { CreateContactModal } from '@/components/CreateContactModal';
import { SuggestButton } from '@/components/SuggestButton';
import { AttachmentPanel } from '@/components/AttachmentPanel';
import { NewLeadModal } from '@/components/NewLeadModal';
import { CreateOpportunityModal } from '@/components/CreateOpportunityModal';
import { CustomFieldsPanel } from '@/components/CustomFieldsPanel';
import { SnapshotPanel } from '@/components/SnapshotPanel';
import { TimelinePanel } from '@/components/TimelinePanel';
import { StakeholderCoveragePanel } from '@/components/StakeholderCoveragePanel';
import { SequenceEnrollmentPanel } from '@/components/SequenceEnrollmentPanel';

interface Account {
  id: string;
  name: string;
  industry: string | null;
  sizeBucket: string | null;
  website: string | null;
  status: string;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  title: string | null;
  status: string;
  /** Story 79.2 — ABM buying-group role. */
  buyingRole: string | null;
}

interface AccountOpportunity {
  id: string;
  name: string;
  status: string;
  estimatedDealValue: string | number;
  currency: string;
  forecastClassification: string;
  progressPercent: number | string;
  expectedCloseDate: string | null;
}

interface AccountLead {
  id: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  status: string;
  source: string;
  channel: string | null;
  campaignId: string | null;
}

export function AccountDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusContactId = searchParams.get('focusContact');
  const [account, setAccount] = useState<Account | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [opportunities, setOpportunities] = useState<AccountOpportunity[]>([]);
  const [leads, setLeads] = useState<AccountLead[]>([]);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showAddOpportunity, setShowAddOpportunity] = useState(false);
  const [showAddLead, setShowAddLead] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<Account>>({});
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  /** Story 77.2 — Intent score chip. Non-fatal: 404/error shows nothing. */
  const [intentScore, setIntentScore] = useState<number | null>(null);
  // Story: contact inline edit. The id of the contact whose row is in
  // edit mode (if any). Pre-populated from ?focusContact=<id> so deep
  // links from lead-detail / campaign-hub land directly in edit.
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const focusedRowRef = useRef<HTMLLIElement | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [acctRes, ctRes, oppRes, leadRes] = await Promise.all([
        apiClient.get<Account>(`/accounts/${id}`),
        apiClient.get<Contact[]>('/contacts', { params: { accountId: id } }),
        apiClient.get<AccountOpportunity[]>('/opportunities', {
          params: { accountId: id, status: 'ALL' },
        }),
        apiClient.get<AccountLead[]>('/leads', { params: { accountId: id } }),
      ]);
      setAccount(acctRes.data);
      setContacts(ctRes.data);
      setOpportunities(oppRes.data);
      setLeads(leadRes.data);
      // Story 77.2 — Non-fatal intent score fetch
      apiClient
        .get<{ score: number }>(`/intent/accounts/${id}/score`)
        .then(({ data }) => setIntentScore(data.score))
        .catch(() => {/* no score yet — chip hidden */});
    } catch {
      setAccount(null);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // When a focusContact param is present and contacts are loaded, open
  // that contact's row in edit mode and scroll it into view.
  useEffect(() => {
    if (!focusContactId || contacts.length === 0) return;
    if (contacts.some((c) => c.id === focusContactId)) {
      setEditingContactId(focusContactId);
      // Defer the scroll to next tick so the edit form has rendered.
      setTimeout(() => {
        focusedRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
  }, [focusContactId, contacts]);

  function clearFocusParam() {
    if (focusContactId) {
      const next = new URLSearchParams(searchParams);
      next.delete('focusContact');
      setSearchParams(next, { replace: true });
    }
  }

  function startEdit() {
    if (!account) return;
    setDraft({
      name: account.name,
      industry: account.industry,
      website: account.website,
      status: account.status,
    });
    setEditError(null);
    setEditing(true);
  }

  async function saveEdit() {
    if (!account) return;
    setSaving(true);
    setEditError(null);
    try {
      const { data } = await apiClient.patch<Account>(`/accounts/${account.id}`, {
        name: draft.name ?? account.name,
        industry: draft.industry?.trim() ? draft.industry : null,
        website: draft.website?.trim() ? draft.website : null,
        status: draft.status ?? account.status,
      });
      setAccount(data);
      setEditing(false);
    } catch {
      setEditError('Failed to save. Try again.');
    } finally {
      setSaving(false);
    }
  }

  if (!account) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  }

  return (
    <div className="space-y-6">
      <CreateContactModal
        open={showAddContact}
        onClose={() => setShowAddContact(false)}
        onCreated={load}
        preselectedAccountId={account.id}
      />
      <CreateOpportunityModal
        open={showAddOpportunity}
        onClose={() => setShowAddOpportunity(false)}
        onCreated={load}
        preselectedAccountId={account.id}
      />
      <NewLeadModal
        open={showAddLead}
        onClose={() => setShowAddLead(false)}
        onCreated={() => load()}
        lockedAccountId={account.id}
        lockedAccountLabel={account.name}
      />

      <Breadcrumbs
        crumbs={[{ label: t('nav.accounts'), to: '/accounts' }, { label: account.name }]}
      />

      <div className="flex items-start justify-between gap-3">
        <h1 className="text-2xl font-semibold">{account.name}</h1>
        {!editing && (
          <Button size="sm" variant="outline" onClick={startEdit}>
            <Pencil className="mr-1 h-3 w-3" /> {t('account.edit')}
          </Button>
        )}
      </div>

      {!editing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <InfoCard label={t('account.industry')} value={account.industry ?? '—'} />
            <InfoCard label={t('account.size')} value={account.sizeBucket ?? '—'} />
            <InfoCard label={t('account.website')} value={account.website ?? '—'} />
            <InfoCard label={t('account.status')} value={account.status} />
          </div>
          {intentScore !== null && <IntentScoreChip score={intentScore} />}
        </div>
      ) : (
        <div className="rounded-lg border bg-card p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium">{t('account.account_name')}</label>
              <Input
                value={draft.name ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs font-medium">{t('account.industry')}</label>
              <div className="flex items-start gap-2">
                <Input
                  value={draft.industry ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, industry: e.target.value }))}
                />
                <SuggestButton
                  entity="ACCOUNT"
                  field="industry"
                  context={{ name: draft.name ?? account.name, website: draft.website ?? account.website }}
                  onAccept={(value) => setDraft((d) => ({ ...d, industry: value }))}
                  emphasize={!draft.industry}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium">{t('account.website')}</label>
              <div className="flex items-start gap-2">
                <Input
                  value={draft.website ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, website: e.target.value }))}
                />
                <SuggestButton
                  entity="ACCOUNT"
                  field="website"
                  context={{ name: draft.name ?? account.name }}
                  onAccept={(value) => setDraft((d) => ({ ...d, website: value }))}
                  emphasize={!draft.website}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium">{t('account.status')}</label>
              <select
                value={draft.status ?? account.status}
                onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                <option value="ACTIVE">{t('account.active')}</option>
                <option value="ARCHIVED">{t('account.archived')}</option>
              </select>
            </div>
          </div>
          {editError && <p className="mt-2 text-xs text-destructive">{editError}</p>}
          <div className="mt-3 flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={saving}>
              {t('account.cancel')}
            </Button>
            <Button size="sm" onClick={saveEdit} disabled={saving}>
              {saving ? t('account.saving') : t('account.save')}
            </Button>
          </div>
        </div>
      )}

      <CustomFieldsPanel entityType="ACCOUNT" entityId={account.id} />

      <SnapshotPanel entityType="ACCOUNT" entityId={account.id} />

      {/* Opportunities for this account */}
      <div className="rounded-lg border bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('account.opportunities_title')} ({opportunities.length})
          </h2>
          <Button size="sm" onClick={() => setShowAddOpportunity(true)}>
            {t('account.add_opportunity')}
          </Button>
        </div>
        {opportunities.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('account.no_opportunities')}</p>
        ) : (
          <ul className="divide-y">
            {opportunities.map((o) => (
              <li
                key={o.id}
                onClick={() => navigate(`/opportunities/${o.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/opportunities/${o.id}`);
                  }
                }}
                role="button"
                tabIndex={0}
                className="flex cursor-pointer items-center justify-between rounded-md px-2 py-3 transition-colors hover:bg-muted/40 focus:bg-muted/40 focus:outline-none"
              >
                <div>
                  <div className="font-medium">{o.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {o.currency}{' '}
                    {Number(o.estimatedDealValue).toLocaleString()} ·{' '}
                    {o.forecastClassification} · {Number(o.progressPercent).toFixed(0)}%
                  </div>
                </div>
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
                  {o.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Leads for this account */}
      <div className="rounded-lg border bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('account.leads_title')} ({leads.length})
          </h2>
          <Button size="sm" onClick={() => setShowAddLead(true)}>
            {t('account.add_lead')}
          </Button>
        </div>
        {leads.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('account.no_leads')}</p>
        ) : (
          <ul className="divide-y">
            {leads.map((l) => (
              <li
                key={l.id}
                onClick={() => navigate(`/leads/${l.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/leads/${l.id}`);
                  }
                }}
                role="button"
                tabIndex={0}
                className="flex cursor-pointer items-center justify-between rounded-md px-2 py-3 transition-colors hover:bg-muted/40 focus:bg-muted/40 focus:outline-none"
              >
                <div>
                  <div className="font-medium">
                    {l.companyName ??
                      [l.firstName, l.lastName].filter(Boolean).join(' ') ??
                      '—'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {l.source}
                    {l.channel ? ` · ${l.channel}` : ''}
                  </div>
                </div>
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
                  {l.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AttachmentPanel entityType="ACCOUNT" entityId={account.id} />

      <TimelinePanel entityType="account" entityId={account.id} />

      {/* Story 79.2 — Stakeholder coverage + ABM score */}
      <StakeholderCoveragePanel accountId={account.id} />

      {/* Story 79.3 — Sequence enrollment */}
      <SequenceEnrollmentPanel accountId={account.id} />

      <div className="rounded-lg border bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('nav.contacts')} ({contacts.length})
          </h2>
          <Button size="sm" onClick={() => setShowAddContact(true)}>
            {t('account.add_contact')}
          </Button>
        </div>
        <ul className="divide-y">
          {contacts.map((c) => (
            <ContactRow
              key={c.id}
              contact={c}
              editing={editingContactId === c.id}
              focused={focusContactId === c.id}
              rowRef={focusContactId === c.id ? focusedRowRef : undefined}
              onStartEdit={() => setEditingContactId(c.id)}
              onCancel={() => {
                setEditingContactId(null);
                clearFocusParam();
              }}
              onSaved={() => {
                setEditingContactId(null);
                clearFocusParam();
                load();
              }}
            />
          ))}
          {contacts.length === 0 && (
            <li className="py-4 text-center text-sm text-muted-foreground">
              {t('account.no_contacts')}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

function ContactRow({
  contact,
  editing,
  focused,
  rowRef,
  onStartEdit,
  onCancel,
  onSaved,
}: {
  contact: Contact;
  editing: boolean;
  focused: boolean;
  rowRef?: React.Ref<HTMLLIElement>;
  onStartEdit: () => void;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState({
    firstName: contact.firstName,
    lastName: contact.lastName,
    title: contact.title ?? '',
    email: contact.email ?? '',
    phone: contact.phone ?? '',
    buyingRole: contact.buyingRole ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset draft when the row enters edit mode (or when a different
  // contact's row is opened next).
  useEffect(() => {
    if (editing) {
      setDraft({
        firstName: contact.firstName,
        lastName: contact.lastName,
        title: contact.title ?? '',
        email: contact.email ?? '',
        phone: contact.phone ?? '',
        buyingRole: contact.buyingRole ?? '',
      });
      setError(null);
    }
  }, [editing, contact]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await apiClient.patch(`/contacts/${contact.id}`, {
        firstName: draft.firstName.trim(),
        lastName: draft.lastName.trim(),
        title: draft.title.trim() || null,
        email: draft.email.trim() || null,
        phone: draft.phone.trim() || null,
        // Story 79.2: empty string sends null (clears the role)
        buyingRole: draft.buyingRole || '',
      });
      onSaved();
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? t('contact.save_error'));
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <li
        ref={rowRef}
        onClick={onStartEdit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onStartEdit();
          }
        }}
        role="button"
        tabIndex={0}
        className={`flex cursor-pointer items-center justify-between py-3 transition-colors hover:bg-muted/40 focus:bg-muted/40 focus:outline-none ${
          focused ? 'bg-muted/30' : ''
        }`}
      >
        <div>
          <div className="font-medium">
            {contact.firstName} {contact.lastName}
          </div>
          <div className="text-xs text-muted-foreground">
            {[contact.title, contact.email, contact.phone].filter(Boolean).join(' · ')}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {contact.buyingRole && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              {t(`buyingRole.${contact.buyingRole}`)}
            </span>
          )}
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
              contact.status === 'ACTIVE'
                ? 'bg-emerald-900/40 text-emerald-200'
                : 'bg-muted text-foreground'
            }`}
          >
            {contact.status}
          </span>
        </div>
      </li>
    );
  }

  return (
    <li ref={rowRef} className="space-y-3 rounded-md bg-muted/30 p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t('contact.edit')}
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Input
          placeholder={t('contact.first_name')}
          value={draft.firstName}
          onChange={(e) => setDraft((d) => ({ ...d, firstName: e.target.value }))}
        />
        <Input
          placeholder={t('contact.last_name')}
          value={draft.lastName}
          onChange={(e) => setDraft((d) => ({ ...d, lastName: e.target.value }))}
        />
        <Input
          placeholder={t('contact.title')}
          value={draft.title}
          onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
        />
        <Input
          placeholder={t('contact.phone')}
          value={draft.phone}
          onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
        />
        <Input
          className="sm:col-span-2"
          placeholder={t('contact.email')}
          value={draft.email}
          onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
        />
        {/* Story 79.2 — buying role selector */}
        <div className="sm:col-span-2">
          <select
            value={draft.buyingRole}
            onChange={(e) => setDraft((d) => ({ ...d, buyingRole: e.target.value }))}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          >
            <option value="">{t('buyingRole.placeholder')}</option>
            <option value="DECISION_MAKER">{t('buyingRole.DECISION_MAKER')}</option>
            <option value="ECONOMIC_BUYER">{t('buyingRole.ECONOMIC_BUYER')}</option>
            <option value="TECHNICAL_BUYER">{t('buyingRole.TECHNICAL_BUYER')}</option>
            <option value="CHAMPION">{t('buyingRole.CHAMPION')}</option>
            <option value="INFLUENCER">{t('buyingRole.INFLUENCER')}</option>
          </select>
        </div>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onCancel} disabled={saving}>
          {t('contact.cancel')}
        </Button>
        <Button size="sm" onClick={save} disabled={saving}>
          {saving ? t('common.loading') : t('contact.save')}
        </Button>
      </div>
    </li>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-semibold">{value}</div>
    </div>
  );
}

/** Story 77.2 — Intent score chip with exponential-decay color coding. */
function IntentScoreChip({ score }: { score: number }) {
  const { t } = useTranslation();
  const color =
    score >= 20
      ? 'bg-green-900/40 text-green-300 border-green-700'
      : score >= 5
      ? 'bg-amber-900/40 text-amber-300 border-amber-700'
      : 'bg-muted text-muted-foreground border-border';
  return (
    <div className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${color}`}>
      <Zap className="h-3.5 w-3.5" />
      {t('intentSignal.intent_score')}: {score.toFixed(1)}
    </div>
  );
}
